import { liquid } from '@codemirror/lang-liquid';
import { Annotation, Compartment, EditorState, type Extension } from '@codemirror/state';
import { EditorView, placeholder as cmPlaceholder } from '@codemirror/view';
import { basicSetup } from 'codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { autocompletion, type Completion, type CompletionContext, snippetCompletion } from '@codemirror/autocomplete';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

type LiquidFilterOptionArgument = {
  name: string;
  type?: string;
  description?: string;
};

type LiquidFilterOption =
  | string
  | {
      name: string;
      detail?: string;
      description?: string;
      args?: LiquidFilterOptionArgument[];
    };

export interface LiquidViewerProps {
  value: string;
  onChange?: (next: string) => void;
  readOnly?: boolean;
  className?: string;
  /** Optional: apply a theme; defaults to oneDark */
  theme?: Extension;
  /** Optional: pass extra extensions (lineWrapping, keymaps, lint, etc.) */
  extensions?: Extension[];
  /** Optional: show a placeholder when empty */
  placeholder?: string;
  /** Optional: focus editor on mount */
  autoFocus?: boolean;
  /** Optional: editor container inline style */
  style?: React.CSSProperties;

  /** Turn autocomplete on/off (default: true) */
  enableAutocomplete?: boolean;
  /** Variables visible in templates: array (flat) or object (nested schema) */
  variables?: string[] | Record<string, any>;
  /** Filters (e.g. date, capitalize…) */
  filters?: LiquidFilterOption[];
  /** Tags (e.g. if, for, render…) */
  tags?: string[];
  /** Optional member schema for `object.` completions (fallback or extra paths) */
  schema?: Record<string, string[]>;
}

export interface LiquidViewerHandle {
  focus(): void;
  getView(): EditorView | null;
}

const LiquidViewer = forwardRef<LiquidViewerHandle, LiquidViewerProps>(
  (
    {
      value,
      onChange,
      readOnly = false,
      className,
      theme = oneDark,
      extensions = [],
      placeholder,
      autoFocus = false,
      style,
      enableAutocomplete = true,
      variables,
      filters,
      tags,
      schema,
    },
    ref,
  ) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef<EditorView | null>(null);

    // Compartments for dynamic reconfiguration
    const editableCompartment = useRef(new Compartment());
    const themeCompartment = useRef(new Compartment());
    const completionCompartment = useRef(new Compartment());

    // Keep onChange fresh without recreating the editor
    const onChangeRef = useRef<typeof onChange>(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    // Annotation to mark programmatic updates
    const ExternalUpdate = useRef(Annotation.define<boolean>()).current;

    function findLiquidContext(state: EditorState, pos: number): 'output' | 'tag' | null {
      const start = Math.max(0, pos - 4000);
      const text = state.sliceDoc(start, pos);
      // Support whitespace-control variants like {{- -}} and {%- -%}
      const lastOpenOutput = Math.max(text.lastIndexOf('{{-'), text.lastIndexOf('{{'));
      const lastOpenTag = Math.max(text.lastIndexOf('{%-'), text.lastIndexOf('{%'));
      const lastCloseOutput = Math.max(text.lastIndexOf('-}}'), text.lastIndexOf('}}'));
      const lastCloseTag = Math.max(text.lastIndexOf('-%}'), text.lastIndexOf('%}'));
      const lastOpen = Math.max(lastOpenOutput, lastOpenTag);
      const lastClose = Math.max(lastCloseOutput, lastCloseTag);
      if (lastOpen < 0 || lastOpen < lastClose) return null;
      return lastOpen === lastOpenOutput ? 'output' : 'tag';
    }

    const toCompletions = (
      list: LiquidFilterOption[] | string[],
      type: Completion['type'],
      defaultDetail?: string,
    ): Completion[] =>
      (list as Array<string | { name: string; detail?: string; description?: string; args?: LiquidFilterOptionArgument[] }>).
        map((entry) => {
          if (typeof entry === 'string') {
            return { label: entry, type, detail: defaultDetail } as Completion;
          }
          const detail = entry.detail ?? defaultDetail;
          const parts: string[] = [];
          if (entry.description) parts.push(entry.description);
          if (entry.args?.length) {
            const argsLine = entry.args
              .map((arg) => `${arg.name}${arg.type ? `: ${arg.type}` : ''}${arg.description ? ` – ${arg.description}` : ''}`)
              .join('\n');
            parts.push(`Arguments:\n${argsLine}`);
          }
          const info = parts.length ? parts.join('\n\n') : undefined;
          return {
            label: entry.name,
            type,
            detail,
            info,
          } as Completion;
        });

    const tagSnippetCompletions: Completion[] = [
      snippetCompletion('{% if ${cond} %}\n  $0\n{% endif %}', { label: 'if … endif', type: 'keyword', detail: 'Conditional' }),
      snippetCompletion('{% for ${item} in ${list} %}\n  $0\n{% endfor %}', { label: 'for … endfor', type: 'keyword', detail: 'Loop' }),
      snippetCompletion('{% case ${expr} %}\n  {% when ${value} %}\n    $0\n{% endcase %}', { label: 'case … endcase', type: 'keyword', detail: 'Switch' }),
    ];

    const isObjectVars = (v: LiquidViewerProps['variables']): v is Record<string, any> => !!v && !Array.isArray(v) && typeof v === 'object';
    const varsTree = isObjectVars(variables) ? (variables as Record<string, any>) : undefined;
    const defaultVariableNames = Array.isArray(variables) && variables.length
      ? variables
      : varsTree
        ? Object.keys(varsTree)
        : ['product', 'collection', 'cart', 'customer', 'settings', 'section', 'shop', 'request', 'template'];

    const defaultFilters: LiquidFilterOption[] = filters && filters.length ? filters : [
      'append','capitalize','date','default','downcase','escape','first','join','last','lstrip','map','minus','plus','divided_by','modulo','pluralize','prepend','remove','replace','reverse','round','ceil','floor','abs','size','slice','sort','split','strip','rstrip','times','toBoolean','toNumber','toString','truncate','uniq','upcase','url_encode'
    ];
    const defaultTags = tags && tags.length ? tags : [
      'if','elsif','endif','for','endfor','case','when','endcase','assign','capture','endcapture','render','include','paginate','endpaginate','comment','endcomment','raw','endraw','cycle','break','continue']
    ;

    const META_KEY = '__meta';

    type LiquidNodeMeta = {
      type?: string;
      description?: string;
      format?: string;
      itemType?: string;
      enumValues?: unknown[];
    };

    const getMeta = (value: any): LiquidNodeMeta | null => {
      if (!value) return null;
      if (Array.isArray(value)) {
        return (value as any)[META_KEY] ?? null;
      }
      if (typeof value === 'object') {
        return (value as any)[META_KEY] ?? null;
      }
      return null;
    };

    const baseTypeOf = (value: any): string => {
      if (Array.isArray(value)) return 'array';
      if (value === null) return 'null';
      const t = typeof value;
      if (t === 'object') return 'object';
      return t;
    };

    const resolveTypeAndDetail = (value: any): { type: string; detail: string } => {
      const meta = getMeta(value);
      let typeLabel = meta?.type ?? baseTypeOf(value);
      if (meta?.type === 'array' && meta.itemType) {
        typeLabel = `${meta.type}<${meta.itemType}>`;
      } else if (!meta?.type && Array.isArray(value)) {
        const childMeta = getMeta(value[0]);
        if (childMeta?.type) {
          typeLabel = `array<${childMeta.type}>`;
        }
      }
      if (meta?.format) {
        typeLabel = `${typeLabel} (${meta.format})`;
      }
      const extras: string[] = [];
      if (meta?.enumValues && meta.enumValues.length) {
        const preview = meta.enumValues.slice(0, 3).map((item) => JSON.stringify(item)).join(' | ');
        extras.push(`enum: ${preview}${meta.enumValues.length > 3 ? ' …' : ''}`);
      }
      if (meta?.description) extras.push(meta.description);
      const detail = extras.length ? `${typeLabel} — ${extras.join(' — ')}` : typeLabel;
      return { type: typeLabel, detail };
    };

    const parsePathSegments = (p: string): string[] => {
      const segs: string[] = [];
      let i = 0; let cur = '';
      const flush = () => { if (cur) segs.push(cur); cur = ''; };
      while (i < p.length) {
        const ch = p[i];
        if (ch === '.') { flush(); i++; continue; }
        if (ch === '[') {
          flush(); i++;
          const q = p[i];
          if (q === '"' || q === "'") { i++; let buf = ''; while (i < p.length && p[i] !== q) buf += p[i++]; if (i < p.length && p[i] === q) i++; if (i < p.length && p[i] === ']') i++; segs.push(buf); continue; }
          let buf = ''; while (i < p.length && p[i] !== ']') buf += p[i++]; if (i < p.length && p[i] === ']') i++; segs.push(buf.trim()); continue;
        }
        cur += ch; i++;
      }
      flush();
      return segs.filter((s) => s.length > 0);
    };

    const getNodeAtPath = (tree: Record<string, any> | undefined, basePath: string) => {
      if (!tree) return undefined;
      if (!basePath) return tree;
      const segs = parsePathSegments(basePath);
      let node: any = tree;
      for (const raw of segs) {
        if (Array.isArray(node)) node = node[0];
        if (!node || typeof node !== 'object') return undefined;
        const seg = String(raw);
        node = node[seg];
      }
      return node;
    };

    const keysAndTypesOf = (node: any): Array<{ key: string; type: string; detail: string }> => {
      if (!node) return [];
      let current = node;
      if (Array.isArray(current)) {
        if (!current.length) return [];
        current = current[0];
      }
      if (!current || typeof current !== 'object') return [];
      return Object.entries(current)
        .filter(([key]) => !key.startsWith('__'))
        .map(([key, value]) => {
          const { type, detail } = resolveTypeAndDetail(value);
          return { key, type, detail };
        });
    };

    const toSchemaObject = (props: string[]) =>
      props.reduce((acc, k) => { (acc as any)[k] = null; return acc; }, {} as Record<string, any>);

    const makeLiquidCompletionSource = (
      varNames: string[],
      varTree: Record<string, any> | undefined,
      flt: LiquidFilterOption[],
      tgs: string[],
      sch?: Record<string, string[]>,
    ) => {
      const extractActiveLoopVars = (text: string) => {
        const res: Array<{ name: string; expr: string }> = [];
        const stack: Array<{ name: string; expr: string }> = [];
        const re = /{%-?\s*(for\s+([A-Za-z_][\w]*)\s+in\s+([^%]+?)|endfor)\s*-?%}/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(text))) {
          if (m[1] && m[1].startsWith('for ')) {
            const name = m[2]!;
            let expr = m[3]!.trim();
            const pipeIdx = expr.indexOf('|'); if (pipeIdx >= 0) expr = expr.slice(0, pipeIdx).trim();
            const wsIdx = expr.search(/\s/); if (wsIdx >= 0) expr = expr.slice(0, wsIdx).trim();
            stack.push({ name, expr });
          } else { stack.pop(); }
        }
        res.push(...stack); return res;
      };

      return (ctx: CompletionContext) => {
        const { state, pos } = ctx;
        const context = findLiquidContext(state, pos);
        if (!context) return null;

        // Try to find the current word/identifier under the cursor
        const word = ctx.matchBefore(/[A-Za-z_][\w.]*/);
        const from = word ? word.from : pos;
        const typed = word ? state.sliceDoc(word.from, pos) : '';
        const options: Completion[] = [];

        // Determine active loop variables in scope
        const before = state.sliceDoc(0, pos);
        const loops = extractActiveLoopVars(before);
        const loopVarMap: Record<string, any> = {};
        for (const { name, expr } of loops) {
          let node = getNodeAtPath(varTree, expr);
          if (!node && sch) {
            const props = sch[expr] || sch[expr?.replace(/\[\]$/, '') as string];
            if (props?.length) node = toSchemaObject(props);
          }
          if (Array.isArray(node)) node = node[0];
          loopVarMap[name] = node;
        }

        // Bracket-notation suggestions must run even when there's no word
        {
          const left = state.sliceDoc(Math.max(0, from - 200), pos);
          const lastBracket = left.lastIndexOf('[');
          const lastCloseBracket = left.lastIndexOf(']');
          if (lastBracket > lastCloseBracket) {
            const baseCandidate = left.slice(0, lastBracket);
            const m = baseCandidate.match(/([A-Za-z_][\w.]+)$/);
            const base = m ? m[1] : '';
            let node: any | undefined;
            if (base && loopVarMap[base]) node = loopVarMap[base];
            if (!node && base) node = getNodeAtPath(varTree, base);
            if (!node && sch && sch[base]?.length) node = toSchemaObject(sch[base]);
            if (node) {
              const kv = keysAndTypesOf(node);
              if (kv.length) {
                return {
                  from: pos,
                  options: kv.map(({ key, type, detail }) => ({
                    label: key,
                    type: 'property',
                    detail: detail ?? type,
                  }) as Completion),
                };
              }
            }
          }
        }

        // If there's still no word and this wasn't bracket context, bail unless explicitly invoked
        if (!word && !ctx.explicit) return null;

        const dot = typed.lastIndexOf('.');
        if (dot > 0) {
          const base = typed.slice(0, dot);
          if (loopVarMap[base]) {
            let node = loopVarMap[base]; if (Array.isArray(node)) node = node[0];
            const kv = keysAndTypesOf(node);
            if (kv.length) {
              options.push(
                ...kv.map(({ key, type, detail }) => ({
                  label: key,
                  type: 'property',
                  detail: detail ?? type,
                }) as Completion),
              );
              return { from: word!.from + dot + 1, options };
            }
          }
          const node = getNodeAtPath(varTree, base);
          if (node) {
            const kv = keysAndTypesOf(node);
            if (kv.length) {
              options.push(
                ...kv.map(({ key, type, detail }) => ({
                  label: key,
                  type: 'property',
                  detail: detail ?? type,
                }) as Completion),
              );
              return { from: word!.from + dot + 1, options };
            }
          }
          if (sch) {
            const props = sch[base];
            if (props && props.length) {
              options.push(...toCompletions(props, 'property'));
              return { from: word!.from + dot + 1, options };
            }
          }
        }

        // (bracket-notation block handled above; keep here disabled)

        if (context === 'tag') {
          options.push(...toCompletions(tgs, 'keyword', 'tag'), ...tagSnippetCompletions);
        } else {
          const left2 = state.sliceDoc(Math.max(0, from - 80), from);
          const afterPipe = /\|\s*$/.test(left2);
          if (afterPipe) {
            options.push(...toCompletions(flt, 'function', 'filter'));
          } else {
            const varNamesWithScope = [...new Set([...Object.keys(loopVarMap), ...varNames])];
            options.push(...toCompletions(varNamesWithScope, 'variable'), ...toCompletions(flt, 'function', 'filter'));
          }
        }

        return { from, options };
      };
    };

    const completionTooltipTheme = EditorView.theme({
      '.cm-tooltip.cm-tooltip-hover, .cm-tooltip.cm-completionInfo': {
        fontFamily: 'var(--font-mono, "SFMono-Regular", "Menlo", "Monaco", "Roboto Mono", ui-monospace, monospace)',
        fontSize: '12px',
        lineHeight: 1.35,
      },
    });

    // Mount once
    useEffect(() => {
      if (!hostRef.current) return;
      const state = EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          liquid(),
          themeCompartment.current.of(theme),
          editableCompartment.current.of(EditorView.editable.of(!readOnly)),
          completionTooltipTheme,
          completionCompartment.current.of(
            enableAutocomplete
              ? autocompletion({
                  override: [
                    makeLiquidCompletionSource(
                      defaultVariableNames,
                      varsTree,
                      defaultFilters,
                      defaultTags,
                      schema,
                    ),
                  ],
                  activateOnTyping: true,
                })
              : ([] as unknown as Extension),
          ),
          ...(placeholder ? [cmPlaceholder(placeholder)] : []),
          EditorView.updateListener.of((update) => {
            if (!update.docChanged) return;
            const isExternal = update.transactions.some((tr) => tr.annotation(ExternalUpdate) === true);
            if (isExternal) return;
            const handler = onChangeRef.current;
            if (handler) handler(update.state.doc.toString());
          }),
          EditorView.theme({ '&': { height: '100%' } }),
          ...extensions,
        ],
      });
      viewRef.current = new EditorView({ state, parent: hostRef.current });
      if (autoFocus) viewRef.current.focus();
      return () => { viewRef.current?.destroy(); viewRef.current = null; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep external value in sync
    useEffect(() => {
      const v = viewRef.current; if (!v) return;
      const current = v.state.doc.toString(); if (value === current) return;
      const sel = v.state.selection.main; const nextAnchor = Math.min(sel.anchor, value.length);
      v.dispatch({ changes: { from: 0, to: current.length, insert: value }, selection: { anchor: nextAnchor }, annotations: ExternalUpdate.of(true) });
    }, [value, ExternalUpdate]);

    // Keep readOnly in sync
    useEffect(() => {
      const v = viewRef.current; if (!v) return;
      v.dispatch({ effects: editableCompartment.current.reconfigure(EditorView.editable.of(!readOnly)) });
    }, [readOnly]);

    // Keep theme in sync
    useEffect(() => {
      const v = viewRef.current; if (!v) return;
      v.dispatch({ effects: themeCompartment.current.reconfigure(theme) });
    }, [theme]);

    // Reconfigure autocomplete when lists/nested vars change
    useEffect(() => {
      const v = viewRef.current; if (!v) return;
      const ext: Extension = enableAutocomplete
        ? autocompletion({
            override: [
              makeLiquidCompletionSource(
                defaultVariableNames,
                varsTree,
                defaultFilters,
                defaultTags,
                schema,
              ),
            ],
            activateOnTyping: true,
          })
        : ([] as unknown as Extension);
      v.dispatch({ effects: completionCompartment.current.reconfigure(ext) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableAutocomplete, schema, JSON.stringify(variables ?? {}), JSON.stringify(filters ?? []), JSON.stringify(tags ?? [])]);

    useImperativeHandle(ref, () => ({ focus: () => viewRef.current?.focus(), getView: () => viewRef.current }), []);

    return (
      <div ref={hostRef} className={className} style={{ width: '100%', height: '100%', ...style }} aria-readonly={readOnly} />
    );
  }
);

export default LiquidViewer;
